import { z } from "zod";
import { appearanceSettingsSchema } from "./appearance";
import {
  safePattern,
  resourceKindSchema,
  datasetSchema,
  orderedDataset,
  assertDataset,
  fieldSchema,
  outputSchema,
  styleSchema,
  parseProject,
  uid,
  type Project,
  type ResourceKind,
} from "./schema";
import { productionSchema } from "./production";
import { impositionSchema } from "./imposition";
import { assignOutputResource } from "./output-workflow";
import { fontFaceSchema, type FontFaceDescriptor } from "./fonts";
import { applyTextStyleSnapshot } from "./text-styles";
import {
  reusableFormSchema,
  reusableRuleSchema,
  reusablePresetSchema,
  applyReusableResource,
} from "./library";
export const resourceRefSchema = z
  .object({
    id: z.string().min(1).max(200),
    version: z.number().int().positive(),
  })
  .strict();
export type ResourceRef = z.infer<typeof resourceRefSchema>;
export const tenantSettingsSchema = z.object({
  tenantId: z.string().min(1),
  revision: z.number().int().nonnegative().default(0),
  revisionQuota: z.number().int().min(0).max(1000).default(20),
  defaultResources: z
    .partialRecord(resourceKindSchema, resourceRefSchema)
    .default({}),
  /** Site and company look of the plug-in modules. */
  appearance: appearanceSettingsSchema.optional(),
});
export type TenantSettings = z.infer<typeof tenantSettingsSchema>;
export interface Resource {
  id: string;
  tenantId: string;
  /** Absent for deliberately shared or historical workspace resources. */
  projectId?: string;
  kind: ResourceKind;
  name: string;
  version: number;
  value: unknown;
  at?: string;
  actor?: string;
  archived?: boolean;
}
export const resourceQuerySchema = z
  .object({
    projectId: z.string().min(1).max(200).optional(),
    scope: z.enum(["project", "shared", "available"]).optional(),
  })
  .strict()
  .refine(
    (query) =>
      !["project", "available"].includes(query.scope || "") ||
      !!query.projectId,
    "Project scope requires a project identity",
  );
export type ResourceQuery = z.infer<typeof resourceQuerySchema>;
export function resourceAvailableToProject(
  resource: Resource,
  project: Pick<Project, "tenantId" | "id">,
) {
  return (
    resource.tenantId === project.tenantId &&
    (!resource.projectId || resource.projectId === project.id)
  );
}
export function filterResources(
  resources: Resource[],
  query: ResourceQuery = {},
) {
  const { projectId, scope = query.projectId ? "project" : undefined } =
    resourceQuerySchema.parse(query);
  return resources.filter((resource) =>
    scope === "shared"
      ? !resource.projectId
      : scope === "project"
        ? resource.projectId === projectId
        : scope === "available"
          ? !resource.projectId || resource.projectId === projectId
          : true,
  );
}
const pageSize = z
  .object({
    width: z.number().positive().max(14400).nullable(),
    height: z.number().positive().max(14400).nullable(),
    margin: z.number().min(0).max(14400).optional(),
  })
  .strict();
const dataset = datasetSchema.extend({
  id: datasetSchema.shape.id.default("catalog-dataset"),
  name: datasetSchema.shape.name.default("Dataset"),
});
const fontResource = z
  .object({
    faceId: z.string(),
    fontManifest: z.array(fontFaceSchema).min(1).max(100),
  })
  .refine(
    (v) => v.fontManifest.some((f) => f.id === v.faceId),
    "Selected font must be in its snapshot",
  );
export function parseResourceValue(
  kind: ResourceKind,
  value: unknown,
): unknown {
  switch (kind) {
    case "form":
      return reusableFormSchema.parse(value);
    case "rule":
      return reusableRuleSchema.parse(value);
    case "preset":
      return reusablePresetSchema.parse(value);
    case "page-size":
      return pageSize.parse(value);
    case "production":
      return productionSchema.parse(value);
    case "output":
      return outputSchema.parse(value);
    case "sheet":
      return impositionSchema.parse(value);
    case "dataset": {
      const v = dataset.parse(value);
      assertDataset(v);
      return orderedDataset(v);
    }
    case "validation": {
      const parsed = fieldSchema.shape.validation
        .unwrap()
        .strict()
        .parse(value);
      if (parsed.pattern) safePattern(parsed.pattern);
      return parsed;
    }
    case "format":
      return fieldSchema.shape.format.unwrap().strict().parse(value);
    case "font":
      return fontResource.parse(value);
    case "style": {
      const wrapped =
        value && typeof value === "object" && "style" in value
          ? (value as { style: unknown; fontManifest: unknown[] })
          : undefined;
      return wrapped
        ? {
            style: styleSchema.parse(wrapped.style),
            fontManifest: z
              .array(fontFaceSchema)
              .max(100)
              .parse(wrapped.fontManifest),
          }
        : styleSchema.parse(value);
    }
  }
}
export function parseResource(resource: Resource): Resource {
  const meta = z
    .object({
      id: z.string().min(1).max(200),
      tenantId: z.string().min(1).max(200),
      projectId: z.string().min(1).max(200).optional(),
      kind: resourceKindSchema,
      name: z.string().min(1).max(200),
      version: z.number().int().nonnegative(),
      at: z.string().optional(),
      actor: z.string().optional(),
    })
    .parse(resource);
  const value = parseResourceValue(meta.kind, resource.value);
  const limit = meta.kind === "output" ? 6000000 : 1000000;
  if (new TextEncoder().encode(JSON.stringify(value)).length > limit)
    throw new Error(
      meta.kind === "output"
        ? "Output profile exceeds 6 MB"
        : "Catalog value exceeds 1 MB",
    );
  return { ...meta, value };
}
export function applyResourceSnapshot(
  project: Project,
  resource: Resource,
  options: { formApplication?: "mapped" | "copy" } = {},
): Project {
  if (resource.tenantId !== project.tenantId)
    throw new Error("Tenant mismatch");
  if (!resourceAvailableToProject(resource, project))
    throw new Error(
      "Resource belongs to another project. Use a shared library entry instead.",
    );
  const r = parseResource(resource),
    value = r.value;
  let p = parseProject(project);
  switch (r.kind) {
    case "form":
    case "rule":
    case "preset":
      p = applyReusableResource(
        p,
        r.kind,
        r.kind === "form" && options.formApplication
          ? { ...(value as object), application: options.formApplication }
          : value,
        r.name,
      );
      if (r.kind === "form")
        p.forms.at(-1)!.profileLibrary = { id: r.id, version: r.version };
      break;
    case "page-size":
      p.pages = p.pages.map((pg) => ({ ...pg, ...pageSize.parse(value) }));
      break;
    case "production":
      p.pages = p.pages.map((pg) => ({
        ...pg,
        production: productionSchema.parse(value),
      }));
      break;
    case "output":
      if (p.outputs.some((o) => o.role)) {
        const preset = outputSchema.parse(value);
        const selected =
          p.outputs.find((o) => o.role === (preset.role || "high-res")) ||
          p.outputs[0];
        p = assignOutputResource(p, selected.id, r);
      } else
        p.outputs = [
          outputSchema.parse({ ...(value as object), id: uid("output") }),
        ];
      break;
    case "sheet":
      p.outputs = p.outputs.map((o) => ({
        ...o,
        ...(o.role === "preview" || o.role === "proof"
          ? {}
          : {
              imposition: impositionSchema.parse(value),
              sheetProfile: { id: r.id, version: r.version, name: r.name },
            }),
      }));
      break;
    case "dataset": {
      const snapshot = {
        ...dataset.parse(value),
        id: `catalog_dataset_${r.id}`,
        name: r.name,
        version: r.version,
      };
      const existing = p.datasets.find(
        (d) => d.id === snapshot.id && d.version === snapshot.version,
      );
      if (existing && JSON.stringify(existing) !== JSON.stringify(snapshot))
        throw new Error("Conflicting immutable dataset snapshot");
      if (!existing) p.datasets.push(snapshot);
      break;
    }
    case "style":
      p = applyTextStyleSnapshot(p, value);
      break;
    case "validation":
      p.fields = p.fields.map((f) => ({
        ...f,
        validation: fieldSchema.shape.validation.parse(value),
      }));
      break;
    case "format":
      p.fields = p.fields.map((f) => ({
        ...f,
        format: fieldSchema.shape.format.parse(value),
      }));
      break;
    case "font": {
      const incoming = fontResource.parse(value),
        faces = [...p.fontManifest];
      for (const face of incoming.fontManifest) {
        const existing = faces.find((f) => f.id === face.id);
        if (existing && JSON.stringify(existing) !== JSON.stringify(face))
          throw new Error("Conflicting immutable font snapshot");
        if (!existing) faces.push(face);
      }
      const face = faces.find((f) => f.id === incoming.faceId)!;
      p.fontManifest = faces;
      p.documentSettings.typography = {
        ...p.documentSettings.typography,
        fontFaceId: face.id,
        fontFamily: face.family,
        fontWeight: face.weight,
      };
      break;
    }
  }
  const { archived, ...snapshot } = r;
  p.resourceSnapshots = [
    ...p.resourceSnapshots.filter(
      (s) =>
        s.kind !== r.kind ||
        s.id !== r.id ||
        (r.kind === "form" && s.version !== r.version),
    ),
    snapshot,
  ];
  return parseProject(p);
}
export function applyFieldResourceDefaults(
  project: Project,
  field: ReturnType<typeof fieldSchema.parse>,
) {
  const next = { ...field };
  for (const snapshot of project.resourceSnapshots) {
    if (snapshot.kind === "validation")
      next.validation = fieldSchema.shape.validation.parse(snapshot.value);
    if (snapshot.kind === "format")
      next.format = fieldSchema.shape.format.parse(snapshot.value);
  }
  return next;
}
